export function isNotEmpty(object) {
  return object !== undefined && object !== null && object.length > 0;
}

export function isNumber(n) {
  return /^-?[\d.]+(?:e-?\d+)?$/.test(n);
}

export function isEmpty(object) {
  return object == null || object.length === 0;
}

export function isOfSufficientLength(object, length) {
  return object !== null && object.length >= length;
}

export function isEqual(object1, object2) {
  return object1 === object2;
}

/**
 * Prepares a file download
 * @param filename the default filename (e.g. 'hello.txt')
 * @param content the file content
 * @param mimeType the mimeType
 */
export function downloadBase64File(filename, content, mimeType) {
  const encodedContent = encodeURIComponent(content);
  const normalizedMimeType = mimeType || "text/plain";

  const element = document.createElement("a");
  element.setAttribute("href", `data:${normalizedMimeType};base64,${encodedContent}`);
  element.setAttribute("download", filename);
  element.style.display = "none";
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
}

export function downloadPlainFile(filename, content, mimeType) {
  const encodedContent = encodeURIComponent(content);
  const normalizedMimeType = mimeType || "text/plain";

  const element = document.createElement("a");
  element.setAttribute("href", `data:${normalizedMimeType};charset=UTF-8,${encodedContent}`);
  element.setAttribute("download", filename);
  element.style.display = "none";
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
}

export function downloadCertFile(filename, content) {
  const encodedContent = encodeURI(content);
  const element = document.createElement("a");
  element.setAttribute("href", `data:application/x-pem-file;charset=UTF-8,${encodedContent}`);
  element.setAttribute("download", filename);
  element.style.display = "none";
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
}

/**
 * Builds a query param string from a pageable object
 * @param pageable the pagination information (e.g. '{page: 1, size: 10, sort: "personName,desc"}')
 * @return string the query params string (e.g. 'page=1&size=10&sort=personName,desc')
 */
export function buildPaginationQueryParams(pageable) {
  if (!pageable) {
    return "";
  }

  const queryParams = [];

  const page = pageable.page;
  if (page !== null && page !== undefined) {
    queryParams.push("page=" + encodeURIComponent(page));
  }

  const size = pageable.size;
  if (size) {
    queryParams.push("size=" + encodeURIComponent(size));
  }

  const sort = pageable.sort;
  if (sort) {
    queryParams.push("sort=" + sort);
  }

  // Default sort by #id
  queryParams.push("sort=id,desc");

  return queryParams.join("&");
}

export function buildSearchCriteriaArray(searchCriteria) {
  const simpleSearchCriteria = [];

  // Should perform a fuzzy 'OR' search on the simple search fields
  const simpleSearchFields = ["serialNumber", "subjectDn", "issuerDn", "certStatus", "certPublicKeyAlgorithm", "certSignatureAlgorithm", "certUsageType"];
  if (isNotEmpty(searchCriteria.simple)) {
    simpleSearchFields.forEach(searchField => {
      let searchValue = searchCriteria.simple;

      // Normalize the human-readable certSignatureAlgorithm (i.e. "SHA256WithRSA" -> "SHA256_WITH_RSA")
      // This is necessary because the user will most probably just copy the i18n name from the table to the simple search field.
      if (searchField === "certSignatureAlgorithm") {
        searchValue = searchValue.replace("With", "_WITH_");
      }

      simpleSearchCriteria.push({
        key: searchField,
        operation: "CONTAINS",
        value: searchValue,
        logicalOperation: "OR"
      });
    });
  }

  const advancedSearchCriteria = [];
  if (isNotEmpty(searchCriteria.serialNumber)) {
    advancedSearchCriteria.push({
      key: "serialNumber",
      operation: "CONTAINS",
      value: searchCriteria.serialNumber,
      logicalOperation: "AND"
    });
  }
  if (isNotEmpty(searchCriteria.certStatus)) {
    let i = 1;
    let length = searchCriteria.certStatus.length;
    searchCriteria.certStatus.forEach(value => {
      if (i === length) {
        //last element should have AND operator so tht the logical operator btw different searchcriteria is "AND"
        advancedSearchCriteria.push({
          key: "certStatus",
          operation: "CONTAINS",
          value: value,
          logicalOperation: "AND"
        });
      } else {
        //same criteria when it has multiple value use "OR"
        advancedSearchCriteria.push({
          key: "certStatus",
          operation: "CONTAINS",
          value: value,
          logicalOperation: "OR"
        });
      }
    });
  }
  if (isNotEmpty(searchCriteria.certPublicKeyAlgorithm)) {
    let i = 1;
    let length = searchCriteria.certPublicKeyAlgorithm.length;
    searchCriteria.certPublicKeyAlgorithm.forEach(value => {
      if (i === length) {
        advancedSearchCriteria.push({
          key: "certPublicKeyAlgorithm",
          operation: "CONTAINS",
          value: value,
          logicalOperation: "AND"
        });
      } else {
        advancedSearchCriteria.push({
          key: "certPublicKeyAlgorithm",
          operation: "CONTAINS",
          value: value,
          logicalOperation: "OR"
        });
      }
    });
  }
  if (isNotEmpty(searchCriteria.certSignatureAlgorithm)) {
    let i = 1;
    let length = searchCriteria.certSignatureAlgorithm.length;
    searchCriteria.certSignatureAlgorithm.forEach(value => {
      if (i === length) {
        advancedSearchCriteria.push({
          key: "certSignatureAlgorithm",
          operation: "CONTAINS",
          value: value,
          logicalOperation: "AND"
        });
      } else {
        advancedSearchCriteria.push({
          key: "certSignatureAlgorithm",
          operation: "CONTAINS",
          value: value,
          logicalOperation: "OR"
        });
      }
    });
  }
  if (isNotEmpty(searchCriteria.validFrom)) {
    advancedSearchCriteria.push({
      key: "validNotBefore",
      operation: "GREATER_THAN",
      value: moment(searchCriteria.validFrom.start).toISOString(),
      logicalOperation: "AND"
    });
    advancedSearchCriteria.push({
      key: "validNotBefore",
      operation: "LESS_THAN",
      value: moment(searchCriteria.validFrom.end).toISOString(),
      logicalOperation: "AND"
    });
  }
  if (isNotEmpty(searchCriteria.validTo)) {
    advancedSearchCriteria.push({
      key: "validNotAfter",
      operation: "GREATER_THAN",
      value: moment(searchCriteria.validTo.start).toISOString(),
      logicalOperation: "AND"
    });
    advancedSearchCriteria.push({
      key: "validNotAfter",
      operation: "LESS_THAN",
      value: moment(searchCriteria.validTo.end).toISOString(),
      logicalOperation: "AND"
    });
  }
  if (isNotEmpty(searchCriteria.subjectDn)) {
    advancedSearchCriteria.push({
      key: "subjectDn",
      operation: "CONTAINS",
      value: searchCriteria.subjectDn,
      logicalOperation: "AND"
    });
  }
  if (isNotEmpty(searchCriteria.issuerDn)) {
    advancedSearchCriteria.push({
      key: "issuerDn",
      operation: "CONTAINS",
      value: searchCriteria.issuerDn,
      logicalOperation: "AND"
    });
  }
  if (isNotEmpty(searchCriteria.certUsageType)) {
    let i = 1;
    let length = searchCriteria.certUsageType.length;
    searchCriteria.certUsageType.forEach(value => {
      if (i === length) {
        advancedSearchCriteria.push({
          key: "certUsageType",
          operation: "CONTAINS",
          value: value,
          logicalOperation: "AND"
        });
      } else {
        advancedSearchCriteria.push({
          key: "certUsageType",
          operation: "CONTAINS",
          value: value,
          logicalOperation: "OR"
        });
      }
    });
  }

  return simpleSearchCriteria.concat(advancedSearchCriteria);
}

/**
 * Converts vue-table 'context' to an API 'pageable' object,
 * i.e. { sortBy, sortDesc, perPage, currentPage } => { page, size, sort }
 * @param pageInfo
 * @return {{size: (*|number), page: number, sort: (*|null)}}
 */
export function buildPageableFromTableContext(pageInfo) {
  const currentPage = parseInt(pageInfo.currentPage || 1);
  const perPage = parseInt(pageInfo.perPage || 20);
  const pageable = {
    page: currentPage - 1,
    size: perPage,
    sort: null
  };
  const sortBy = pageInfo.sortBy;
  if (sortBy) {
    const sortDesc = pageInfo.sortDesc;
    pageable.sort = `${sortBy},${sortDesc ? "desc" : "asc"}`;
  }

  return pageable;
}

/**
 * Normalizes and cleans up vue-table 'context'.
 * @param pageInfo
 */
export function normalizeTableContext(pageInfo) {
  const queryParamsObject = {};

  if (pageInfo.sortBy) {
    queryParamsObject.sortBy = pageInfo.sortBy;
    queryParamsObject.sortDesc = !!pageInfo.sortDesc;
  }
  if (pageInfo.perPage) {
    queryParamsObject.perPage = pageInfo.perPage;
  }
  if (pageInfo.currentPage) {
    queryParamsObject.currentPage = pageInfo.currentPage;
  }

  return queryParamsObject;
}

/**
 * Parses a certificate Subject DN (string -> object)
 * @param subjectDn the distinguished name as string (e.g. "C=DE,O=MTG AG,OU=Development,CN=Common name")
 * @return {{}} the certificate DN as object (e.g. {c="DE", o="MTG AG", ou="Development", cn = "Common name"})
 */
export function parseSubjectDnString(subjectDn) {
  if (!subjectDn || subjectDn.length === 0) {
    throw new Error("Subject DN cannot be empty");
  }
  const subjectDnArray = subjectDn.split(",");
  return subjectDnArray.reduce((obj, item) => {
    if (!item) {
      return obj;
    }
    const keyValuePair = item.split("=");
    if (keyValuePair.length === 0) {
      return obj;
    }
    const [key, value] = keyValuePair;
    obj[key] = value;
    return obj;
  }, {});
}

/**
 * Converts an array of items to CSV.
 * @param headers the CSV headers as an array, e.g. ["id","subjectDn"]
 * @param items the objects to convert to rows as an array of objects, e.g. [{id, subjectDn},{id, subjectDn}]
 * @return {string} the CSV content
 */
export function buildCsv(headers, items) {
  const separatorCharacter = getListSeparator();
  const csvRows = [headers.join(separatorCharacter)];
  items.forEach(item => {
    const row = [];
    headers.forEach(header => {
      row.push('"' + item[header] + '"' || "");
    });
    csvRows.push(row.join(separatorCharacter));
  });
  return csvRows.join("\r\n");
}

function getListSeparator() {
  const list = ["a", "b"];
  if (list.toLocaleString) {
    const str = list.toLocaleString();
    if (str.includes(";") && !str.includes(",")) {
      return ";";
    }
  }
  return ",";
}

/**
 * Method to change the text to camel case
 * e.g input  REQUEST_ACTIVATED
 * e.g output Request activated
 */
export function toCamelCase(input) {
  return input
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
      return index === 0 ? word.toUpperCase() : word.toLowerCase();
    })
    .replace(/_/g, " ");
}

/**
 * Method to check if password has 1 upper case ,1 number and 1 special character
 * @param input password to check
 * @returns {*} true or false
 */
export function passwordPatternMatch(input) {
  return input.match("^(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]");
}

export function resetValidityClassFromElement(elem) {
  elem.classList.remove("is-invalid");
}

/**
 * Constants to access it globally
 * @type {{DATE_TIME_PATTERN: string, DATE_PATTERN: string}}
 */
export const constants = {
  DATE_PATTERN: "YYYY-MM-DD",
  DATE_TIME_PATTERN: "YYYY-MM-DD hh:mm"
};

import moment from "moment";
